import {
  ChangeDetectionStrategy, Component, EventEmitter, Output, signal, inject, OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AdminService, AdminForbiddenError } from '../../services/admin.service.js';
import { FirebaseService } from '../../services/firebase.service.js';
import type {
  HealthReport, HealthCheck, QuotaKind, QuotaDefaultsDoc, QuotaDefaultsPatchRequest,
} from '../../models/admin.types.js';

type QuotaInputValue = number | null | '';
type TierInputs = Record<QuotaKind, QuotaInputValue>;

function emptyTierInputs(): TierInputs {
  return { aiQuick: '', aiDeep: '', aiSearch: '', clip: '', zip: '' };
}

@Component({
  selector: 'app-admin-tools',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="p-6 space-y-6">
      <!-- Health -->
      <section>
        <div class="flex items-center justify-between mb-3">
          <h3 class="text-sm font-bold text-gray-200 uppercase tracking-wider">Здоровье сервисов</h3>
          <button (click)="reloadHealth()" [disabled]="loadingHealth()"
                  class="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 text-gray-200 text-xs font-semibold rounded border border-gray-700">
            {{ loadingHealth() ? 'проверяю…' : 'обновить' }}
          </button>
        </div>
        @if (health(); as h) {
          <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div [class]="cardClass(h.checks.postgres)">
              <p class="text-xs text-gray-300 uppercase tracking-wider font-semibold">PostgreSQL</p>
              <p class="text-sm font-semibold mt-1">{{ statusLabel(h.checks.postgres) }}</p>
              @if (h.checks.postgres?.latencyMs !== undefined) {
                <p class="text-xs text-gray-400 mt-0.5">{{ h.checks.postgres?.latencyMs }} ms</p>
              }
              @if (h.checks.postgres?.error) {
                <p class="text-xs text-red-300 truncate mt-0.5 font-mono">{{ h.checks.postgres?.error }}</p>
              }
            </div>
            <div [class]="cardClass(h.checks.firestore)">
              <p class="text-xs text-gray-300 uppercase tracking-wider font-semibold">Firestore</p>
              <p class="text-sm font-semibold mt-1">{{ statusLabel(h.checks.firestore) }}</p>
              @if (h.checks.firestore?.latencyMs !== undefined) {
                <p class="text-xs text-gray-400 mt-0.5">{{ h.checks.firestore?.latencyMs }} ms</p>
              }
            </div>
            <div [class]="cardClass(h.checks.cloudRunApi)">
              <p class="text-xs text-gray-300 uppercase tracking-wider font-semibold">cloud-run-api</p>
              <p class="text-sm font-semibold mt-1">{{ statusLabel(h.checks.cloudRunApi) }}</p>
              @if (h.checks.cloudRunApi?.latencyMs !== undefined) {
                <p class="text-xs text-gray-400 mt-0.5">{{ h.checks.cloudRunApi?.latencyMs }} ms</p>
              }
            </div>
            <div [class]="cardClass(h.checks.cloudRunWorker)">
              <p class="text-xs text-gray-300 uppercase tracking-wider font-semibold">cloud-run-worker</p>
              <p class="text-sm font-semibold mt-1">{{ statusLabel(h.checks.cloudRunWorker) }}</p>
              @if (h.checks.cloudRunWorker?.latencyMs !== undefined) {
                <p class="text-xs text-gray-400 mt-0.5">{{ h.checks.cloudRunWorker?.latencyMs }} ms</p>
              }
            </div>
          </div>
        } @else if (loadingHealth()) {
          <p class="text-xs text-gray-500 italic">Загрузка…</p>
        }
      </section>

      <!-- Broadcast banner -->
      <section>
        <h3 class="text-sm font-bold text-gray-200 uppercase tracking-wider mb-3">Транслировать баннер</h3>

        <!-- Current banner indicator (live via firebase.systemBanner signal) -->
        @if (currentBanner(); as b) {
          <div class="bg-amber-950/30 border border-amber-700/40 rounded-lg p-3 mb-3 flex items-start gap-3">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-amber-400 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <div class="flex-1 min-w-0">
              <p class="text-xs text-amber-300 uppercase tracking-wider font-semibold mb-1">Активный баннер · {{ b.severity }}</p>
              <p class="text-sm text-amber-100 break-words">{{ b.message }}</p>
              <p class="text-xs text-amber-300/70 mt-1">истекает {{ formatExpires(b.expiresAt) }}</p>
            </div>
            <button (click)="loadCurrentIntoForm()" [disabled]="busyBanner()"
                    class="shrink-0 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 text-gray-200 text-xs font-semibold rounded border border-gray-700 transition-colors">
              В форму
            </button>
          </div>
        } @else {
          <p class="text-xs text-gray-400 italic mb-3">Активного баннера нет</p>
        }

        <div class="bg-gray-950 border border-gray-800 rounded-lg p-4 space-y-3">
          <div class="flex flex-col gap-1">
            <label class="text-xs text-gray-300 uppercase tracking-wider font-semibold">Сообщение</label>
            <textarea [value]="bannerMessage()" (input)="bannerMessage.set($any($event.target).value)"
                      placeholder="Например: техобслуживание 18:00–19:00"
                      maxlength="280" rows="2"
                      class="bg-gray-900 border border-gray-800 rounded px-3 py-2 text-sm text-gray-100 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none resize-none"></textarea>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div class="flex flex-col gap-1">
              <label class="text-xs text-gray-300 uppercase tracking-wider font-semibold">Уровень</label>
              <select [value]="bannerSeverity()" (change)="bannerSeverity.set($any($event.target).value)"
                      class="bg-gray-900 border border-gray-800 rounded px-2 py-1.5 text-sm text-gray-100 focus:border-purple-500 outline-none">
                <option value="info">info (синий)</option>
                <option value="warning">warning (амбер)</option>
              </select>
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-xs text-gray-300 uppercase tracking-wider font-semibold">Истекает</label>
              <input type="datetime-local" [value]="bannerExpiresAt()"
                     (input)="bannerExpiresAt.set($any($event.target).value)"
                     class="bg-gray-900 border border-gray-800 rounded px-2 py-1.5 text-sm text-gray-100 focus:border-purple-500 outline-none" />
            </div>
          </div>
          <div class="flex gap-2">
            <button (click)="sendBanner()" [disabled]="busyBanner() || !bannerMessage().trim() || !bannerExpiresAt()"
                    class="flex-1 px-4 py-2 bg-amber-600/30 hover:bg-amber-600/50 disabled:opacity-50 text-amber-200 border border-amber-700/50 rounded text-sm font-semibold">
              {{ busyBanner() ? '…' : 'Опубликовать' }}
            </button>
            <button (click)="clearBanner()" [disabled]="busyBanner()"
                    class="px-4 py-2 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 text-gray-300 border border-gray-700 rounded text-sm font-semibold">
              Снять
            </button>
          </div>
          @if (bannerError()) {
            <p class="text-xs text-red-400">{{ bannerError() }}</p>
          }
          @if (bannerMessage_status()) {
            <p class="text-xs text-emerald-400">{{ bannerMessage_status() }}</p>
          }
        </div>
      </section>

      <!-- Quota defaults -->
      <section>
        <div class="flex items-center justify-between mb-3">
          <h3 class="text-sm font-bold text-gray-200 uppercase tracking-wider">Глобальные дефолты квот</h3>
          <button (click)="reloadQuotaDefaults()" [disabled]="loadingDefaults()"
                  class="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 text-gray-200 text-xs font-semibold rounded border border-gray-700">
            {{ loadingDefaults() ? 'загрузка…' : 'обновить' }}
          </button>
        </div>
        <div class="bg-gray-950 border border-gray-800 rounded-lg p-4 space-y-3">
          <p class="text-xs text-gray-400">
            Применяется ко всем пользователям без персонального override. Изменения вступают в силу в течение
            <strong>60 секунд</strong> (per-instance cache TTL Cloud Run).
          </p>
          <table class="w-full text-xs border border-gray-800 rounded overflow-hidden">
            <thead class="bg-gray-900/60 text-gray-400 uppercase tracking-wider text-[10px]">
              <tr>
                <th class="text-left px-2 py-1 font-medium">Тип</th>
                <th class="text-left px-2 py-1 font-medium">Free</th>
                <th class="text-left px-2 py-1 font-medium">Pro</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-900">
              @for (k of QUOTA_KINDS; track k) {
                <tr>
                  <td class="px-2 py-1 text-gray-400">{{ kindLabel(k) }}</td>
                  <td class="px-2 py-1">
                    <input type="text" inputmode="numeric"
                           [value]="renderTierInput(freeInputs()[k])"
                           (input)="onTierInput('free', k, $any($event.target).value)"
                           placeholder="(без изменений)"
                           class="w-24 bg-gray-900 border border-gray-800 rounded px-1.5 py-0.5 text-xs font-mono text-gray-200 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"/>
                  </td>
                  <td class="px-2 py-1">
                    <input type="text" inputmode="numeric"
                           [value]="renderTierInput(proInputs()[k])"
                           (input)="onTierInput('pro', k, $any($event.target).value)"
                           placeholder="(без изменений)"
                           class="w-24 bg-gray-900 border border-gray-800 rounded px-1.5 py-0.5 text-xs font-mono text-gray-200 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"/>
                  </td>
                </tr>
              }
            </tbody>
          </table>
          <p class="text-[10px] text-gray-500">
            Целое ≥ 0 = жёсткий лимит. <code>null</code> = безлимит. Пусто = не менять.
            Текущее: <span class="text-gray-300">Free Quick={{ formatCurrent(currentDefaults()?.free?.aiQuick) }} · Deep={{ formatCurrent(currentDefaults()?.free?.aiDeep) }} · Search={{ formatCurrent(currentDefaults()?.free?.aiSearch) }} · Clip={{ formatCurrent(currentDefaults()?.free?.clip) }} · ZIP={{ formatCurrent(currentDefaults()?.free?.zip) }}</span>
            <span class="text-gray-300"> · Pro Quick={{ formatCurrent(currentDefaults()?.pro?.aiQuick) }} · Deep={{ formatCurrent(currentDefaults()?.pro?.aiDeep) }} · Search={{ formatCurrent(currentDefaults()?.pro?.aiSearch) }} · Clip={{ formatCurrent(currentDefaults()?.pro?.clip) }} · ZIP={{ formatCurrent(currentDefaults()?.pro?.zip) }}</span>
          </p>
          <div class="flex gap-2">
            <button (click)="saveQuotaDefaults()" [disabled]="busyDefaults() || !hasQuotaEdits()"
                    class="flex-1 px-4 py-2 bg-purple-600/30 hover:bg-purple-600/50 disabled:opacity-50 disabled:cursor-not-allowed text-purple-200 border border-purple-700/50 rounded text-sm font-semibold">
              {{ busyDefaults() ? '…' : 'Сохранить дефолты' }}
            </button>
            <button (click)="resetQuotaForm()" [disabled]="busyDefaults() || !hasQuotaEdits()"
                    class="px-4 py-2 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-300 border border-gray-700 rounded text-sm font-semibold">
              Сбросить форму
            </button>
          </div>
          @if (defaultsError()) {
            <p class="text-xs text-red-400">{{ defaultsError() }}</p>
          }
          @if (defaultsStatus()) {
            <p class="text-xs text-emerald-400">{{ defaultsStatus() }}</p>
          }
        </div>
      </section>
    </div>
  `,
})
export class AdminToolsComponent implements OnInit {
  @Output() forbidden = new EventEmitter<void>();
  private adminService = inject(AdminService);
  private firebase = inject(FirebaseService);

  health = signal<HealthReport | null>(null);
  loadingHealth = signal(false);

  // Live state from Firestore system/banner doc — drives the «Активный баннер» card.
  currentBanner = this.firebase.systemBanner;

  bannerMessage = signal('');
  bannerSeverity = signal<'info' | 'warning'>('info');
  bannerExpiresAt = signal(this.defaultExpiresLocal());
  busyBanner = signal(false);
  bannerError = signal<string | null>(null);
  bannerMessage_status = signal<string | null>(null);

  // ── Quota defaults form ───────────────────────────────────────────────
  readonly QUOTA_KINDS: QuotaKind[] = ['aiQuick', 'aiDeep', 'aiSearch', 'clip', 'zip'];
  currentDefaults = signal<QuotaDefaultsDoc | null>(null);
  freeInputs = signal<TierInputs>(emptyTierInputs());
  proInputs = signal<TierInputs>(emptyTierInputs());
  loadingDefaults = signal(false);
  busyDefaults = signal(false);
  defaultsError = signal<string | null>(null);
  defaultsStatus = signal<string | null>(null);

  ngOnInit() {
    this.reloadHealth();
    this.reloadQuotaDefaults();
    // Prefill form from active banner ONCE on mount — operator can edit and
    // re-publish without retyping. After this, the form lives independently
    // (Снять/Опубликовать are explicit ops; we don't auto-sync the form to
    // signal changes, otherwise editing a long message would be clobbered when
    // someone else re-broadcasts).
    this.loadCurrentIntoForm();
  }

  loadCurrentIntoForm() {
    const b = this.currentBanner();
    if (!b) return;
    this.bannerMessage.set(b.message);
    this.bannerSeverity.set(b.severity);
    this.bannerExpiresAt.set(this.toLocalDateTime(b.expiresAt));
  }

  formatExpires(iso: string): string {
    try { return new Date(iso).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' }); }
    catch { return iso; }
  }

  private toLocalDateTime(iso: string): string {
    try {
      const d = new Date(iso);
      const pad = (n: number) => n.toString().padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return ''; }
  }

  async reloadHealth() {
    this.loadingHealth.set(true);
    try {
      const h = await this.adminService.getHealth();
      this.health.set(h);
    } catch (err: any) {
      if (err instanceof AdminForbiddenError) { this.forbidden.emit(); return; }
      this.health.set({ ok: false, checks: {} });
    } finally {
      this.loadingHealth.set(false);
    }
  }

  cardClass(c: HealthCheck | undefined): string {
    if (!c) return 'bg-gray-950 border border-gray-800 rounded-lg p-3';
    return c.ok
      ? 'bg-emerald-950/30 border border-emerald-800/50 rounded-lg p-3'
      : 'bg-red-950/30 border border-red-800/50 rounded-lg p-3';
  }

  statusLabel(c: HealthCheck | undefined): string {
    if (!c) return '—';
    return c.ok ? 'OK' : 'FAIL';
  }

  async sendBanner() {
    this.busyBanner.set(true);
    this.bannerError.set(null);
    this.bannerMessage_status.set(null);
    try {
      const expiresIso = new Date(this.bannerExpiresAt()).toISOString();
      await this.adminService.setBanner({
        message: this.bannerMessage().trim(),
        severity: this.bannerSeverity(),
        expiresAt: expiresIso,
      });
      this.bannerMessage_status.set('Баннер опубликован');
    } catch (err: any) {
      if (err instanceof AdminForbiddenError) { this.forbidden.emit(); return; }
      this.bannerError.set(err?.message || 'Ошибка');
    } finally {
      this.busyBanner.set(false);
    }
  }

  async clearBanner() {
    if (!confirm('Снять текущий баннер?')) return;
    this.busyBanner.set(true);
    this.bannerError.set(null);
    this.bannerMessage_status.set(null);
    try {
      await this.adminService.clearBanner();
      this.bannerMessage.set('');
      this.bannerMessage_status.set('Баннер снят');
    } catch (err: any) {
      if (err instanceof AdminForbiddenError) { this.forbidden.emit(); return; }
      this.bannerError.set(err?.message || 'Ошибка');
    } finally {
      this.busyBanner.set(false);
    }
  }

  // datetime-local needs YYYY-MM-DDTHH:mm in local TZ
  private defaultExpiresLocal(): string {
    const d = new Date(Date.now() + 60 * 60 * 1000); // +1h default
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  // ── Quota defaults ────────────────────────────────────────────────────
  kindLabel(k: QuotaKind): string {
    return ({ aiQuick: 'AI Quick', aiDeep: 'AI Deep', aiSearch: 'AI Search', clip: 'Клип', zip: 'ZIP' } as Record<QuotaKind, string>)[k];
  }

  formatCurrent(v: number | null | undefined): string {
    if (v === null) return '∞';
    if (v === undefined) return '—';
    return String(v);
  }

  /** Renders a tier input value back into a string for the textbox. */
  renderTierInput(v: QuotaInputValue): string {
    if (v === '') return '';
    if (v === null) return 'null';
    return String(v);
  }

  /**
   * Stage a tier+kind edit. Empty = no change. `null` (literal text) =
   * unlimited. Integer ≥ 0 = cap. Anything else = invalid → drop edit.
   */
  onTierInput(tier: 'free' | 'pro', kind: QuotaKind, raw: string) {
    const trimmed = (raw || '').trim();
    let value: QuotaInputValue;
    if (trimmed === '') value = '';
    else if (trimmed.toLowerCase() === 'null') value = null;
    else {
      const n = Number(trimmed);
      if (Number.isInteger(n) && n >= 0) value = n;
      else value = ''; // invalid input — silently drop
    }
    const target = tier === 'free' ? this.freeInputs : this.proInputs;
    target.set({ ...target(), [kind]: value });
  }

  hasQuotaEdits(): boolean {
    const free = this.freeInputs();
    const pro = this.proInputs();
    return this.QUOTA_KINDS.some((k) => free[k] !== '' || pro[k] !== '');
  }

  resetQuotaForm() {
    this.freeInputs.set(emptyTierInputs());
    this.proInputs.set(emptyTierInputs());
    this.defaultsError.set(null);
    this.defaultsStatus.set(null);
  }

  async reloadQuotaDefaults() {
    this.loadingDefaults.set(true);
    try {
      const doc = await this.adminService.getQuotaDefaults();
      this.currentDefaults.set(doc);
    } catch (err: any) {
      console.warn('[admin-tools] failed to load quota defaults:', err.message);
      this.currentDefaults.set(null);
    } finally {
      this.loadingDefaults.set(false);
    }
  }

  async saveQuotaDefaults() {
    if (!this.hasQuotaEdits()) return;
    if (!confirm('Применить новые глобальные дефолты квот? Изменения вступят в силу в течение ~60с.')) return;
    this.busyDefaults.set(true);
    this.defaultsError.set(null);
    this.defaultsStatus.set(null);
    try {
      const body: QuotaDefaultsPatchRequest = {};
      const free: Partial<QuotaDefaultsDoc['free']> = {};
      const pro: Partial<QuotaDefaultsDoc['pro']> = {};
      for (const k of this.QUOTA_KINDS) {
        const fv = this.freeInputs()[k];
        if (fv !== '') (free as any)[k] = fv;
        const pv = this.proInputs()[k];
        if (pv !== '') (pro as any)[k] = pv;
      }
      if (Object.keys(free).length) body.free = free;
      if (Object.keys(pro).length) body.pro = pro;

      const result = await this.adminService.setQuotaDefaults(body);
      this.currentDefaults.set(result.defaults);
      this.resetQuotaForm();
      this.defaultsStatus.set(`Сохранено. Распространение в течение ${result.propagation_seconds}с.`);
    } catch (err: any) {
      if (err instanceof AdminForbiddenError) { this.forbidden.emit(); return; }
      this.defaultsError.set(err?.message || 'Ошибка сохранения');
    } finally {
      this.busyDefaults.set(false);
    }
  }
}
